import { Spinner, TableCell, TableHead, TableRow } from '@/components/ui'
import { cn } from '@/lib/utils'
import { format as formatLocalized } from '@/utils/date-fns/format'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { addDays, parseISO } from 'date-fns'
import { ChevronRightIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { InfoLabel } from '../driver-grade/grade-parts'
import {
  filterDrivers,
  useDriverWorkFilter,
  useDriverWorkFilterForm,
} from './driver-work-filter'
import { DriverWorkLayout } from './driver-work-header'
import { formatHours } from './hours'
import { useRowSort } from './row-sort'
import { ScrollTable } from './scroll-table'
import { DRIVER_WORK_DAYS_QUERY, DRIVER_WORK_YEAR_QUERY } from './queries'
import { type YearWeek, yearWeeks } from './year-weeks'

type YearRow = ResultOf<typeof DRIVER_WORK_YEAR_QUERY>['driverWorkYear'][number]

interface HoursRow {
  totalHours: number
  driveHours: number
  workHours: number
  availableHours: number
}

const LEVEL_PADDING = ['pl-5', 'pl-11', 'pl-17']

const hours = (value: number) => (value ? formatHours(value) : '–')

const HoursCells: React.FC<{ row: HoursRow; days?: number | null }> = ({
  row,
  days,
}) => (
  <>
    <TableCell end className="font-semibold tabular-nums">
      {hours(row.totalHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {hours(row.driveHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {hours(row.workHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {hours(row.availableHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {days ?? ''}
    </TableCell>
  </>
)

const ToggleLabel: React.FC<{
  open: boolean
  level: number
  children: React.ReactNode
}> = ({ open, level, children }) => (
  <TableCell className={cn('pr-4', LEVEL_PADDING[level])}>
    <span className="inline-flex items-center gap-2">
      <ChevronRightIcon
        className={cn(
          'h-4 w-4 shrink-0 text-stone-400 transition-transform',
          open && 'rotate-90'
        )}
      />
      {children}
    </span>
  </TableCell>
)

const clock = (value: string | null | undefined) =>
  value ? formatLocalized(parseISO(value), 'HH:mm') : ''

const WeekDayRows: React.FC<{ user: string; weekStart: string }> = ({
  user,
  weekStart,
}) => {
  const { year, ownedVehiclesOnly } = useDriverWorkFilter().filter
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_WORK_DAYS_QUERY, {
    variables: { user, weekStart, year, ownedVehiclesOnly },
  })
  const data = current ?? previousData

  if (loading && !data)
    return (
      <TableRow className="bg-stone-50">
        <TableCell colSpan={7} className={LEVEL_PADDING[2]}>
          <Spinner className="size-4" />
        </TableCell>
      </TableRow>
    )

  return (
    <>
      {(data?.driverWorkDays ?? []).map(day => {
        const date = parseISO(day.date)
        const weekend = [0, 6].includes(date.getDay())
        return (
          <TableRow
            key={day.date}
            className={cn(
              'bg-stone-50 text-sm',
              !day.totalHours && 'text-stone-400'
            )}
          >
            <TableCell
              className={cn(
                LEVEL_PADDING[2],
                'capitalize',
                weekend && 'text-stone-500'
              )}
            >
              {formatLocalized(date, 'EEE d. MMM')}
            </TableCell>
            <TableCell className="tabular-nums">
              {day.firstStart
                ? `${clock(day.firstStart)} – ${clock(day.lastEnd)}`
                : ''}
            </TableCell>
            <HoursCells row={day} />
          </TableRow>
        )
      })}
    </>
  )
}

const WeekRows: React.FC<{
  user: string
  week: YearWeek
  open: boolean
  onToggle(): void
}> = ({ user, week, open, onToggle }) => {
  const translations = useTranslate({ week: 'common.week' })
  const monday = parseISO(week.weekStart)

  return (
    <>
      <TableRow
        className={cn(
          'cursor-pointer bg-stone-50/60 hover:bg-stone-100',
          !week.totalHours && 'text-stone-400'
        )}
        onClick={onToggle}
      >
        <ToggleLabel open={open} level={1}>
          <span>
            {translations.week} {week.isoWeek}
            <span className="ml-2 text-xs text-stone-400">
              {formatLocalized(monday, 'd. MMM')} –{' '}
              {formatLocalized(addDays(monday, 6), 'd. MMM')}
            </span>
          </span>
        </ToggleLabel>
        <TableCell />
        <HoursCells row={week} days={week.days} />
      </TableRow>
      {open && <WeekDayRows user={user} weekStart={week.weekStart} />}
    </>
  )
}

const DriverRows: React.FC<{
  driver: YearRow
  year: number
  open: boolean
  openWeeks: Set<string>
  onToggle(key: string): void
}> = ({ driver, year, open, openWeeks, onToggle }) => {
  const weeks = useMemo(
    () => yearWeeks(year, driver.weekTotals),
    [year, driver]
  )

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-stone-50"
        onClick={() => onToggle(driver.user.id)}
      >
        <ToggleLabel open={open} level={0}>
          <span className="font-semibold text-stone-900">
            {driver.user.fullName}
          </span>
        </ToggleLabel>
        <TableCell />
        <HoursCells row={driver} days={driver.days} />
      </TableRow>
      {open &&
        weeks.map(week => {
          const key = `${driver.user.id}:${week.weekStart}`
          return (
            <WeekRows
              key={key}
              user={driver.user.id}
              week={week}
              open={openWeeks.has(key)}
              onToggle={() => onToggle(key)}
            />
          )
        })}
    </>
  )
}

const DriverWorkTreeTable: React.FC<{
  rows: YearRow[]
  year: number
  loading: boolean
}> = ({ rows, year, loading }) => {
  const translations = useTranslate({
    level: 'driver-work.driver-week-day',
    firstLast: 'driver-work.first-last',
    total: 'driver-work.total',
    totalInfo: 'driver-work.total-info',
    driving: 'common.driving',
    otherWork: 'common.other-work',
    availability: 'common.availability',
    days: 'driver-work.days-worked',
    noData: 'driver-work.no-data',
  })
  const [open, setOpen] = useState<Set<string>>(new Set())
  const { sorted, headProps } = useRowSort(rows, {
    name: row => row.user.fullName,
    total: row => row.totalHours,
    drive: row => row.driveHours,
    work: row => row.workHours,
    available: row => row.availableHours,
    days: row => row.days,
  })

  const toggle = (key: string) =>
    setOpen(previous => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <ScrollTable
      header={
        <TableRow>
          <TableHead {...headProps('name')}>{translations.level}</TableHead>
          <TableHead>{translations.firstLast}</TableHead>
          <TableHead end {...headProps('total')}>
            <InfoLabel
              plain
              label={translations.total}
              info={translations.totalInfo}
            />
          </TableHead>
          <TableHead end {...headProps('drive')}>
            {translations.driving}
          </TableHead>
          <TableHead end {...headProps('work')}>
            {translations.otherWork}
          </TableHead>
          <TableHead end {...headProps('available')}>
            {translations.availability}
          </TableHead>
          <TableHead end {...headProps('days')}>
            {translations.days}
          </TableHead>
        </TableRow>
      }
    >
      {loading && (
        <TableRow>
          <TableCell colSpan={7}>
            <Spinner />
          </TableCell>
        </TableRow>
      )}
      {!loading && !rows.length && (
        <TableRow>
          <TableCell colSpan={7} className="text-center text-stone-500">
            {translations.noData}
          </TableCell>
        </TableRow>
      )}
      {sorted.map(driver => (
        <DriverRows
          key={driver.user.id}
          driver={driver}
          year={year}
          open={open.has(driver.user.id)}
          openWeeks={open}
          onToggle={toggle}
        />
      ))}
    </ScrollTable>
  )
}

export const DriverWorkTablePage: React.FC = () => {
  const form = useDriverWorkFilterForm()
  const { year, drivers, ownedVehiclesOnly } = form.filter
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_WORK_YEAR_QUERY, {
    variables: { year, ownedVehiclesOnly },
  })
  const data = current ?? previousData
  const rows = useMemo(
    () => filterDrivers(data?.driverWorkYear ?? [], drivers),
    [data, drivers]
  )

  return (
    <DriverWorkLayout form={form}>
      <DriverWorkTreeTable
        key={year}
        rows={rows}
        year={year}
        loading={loading && !data}
      />
    </DriverWorkLayout>
  )
}
